import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { MoreVertical, Zap } from "lucide-react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { norm } from "@/lib/normalize";
import { formatINR } from "@/lib/currency";

const STATUS_COLORS = {
  scheduled: "bg-[rgba(197,160,89,0.15)] text-gold border-gold-subtle",
  completed: "bg-[rgba(46,125,50,0.2)] text-green-300 border-green-800",
  cancelled: "bg-[rgba(211,47,47,0.15)] text-red-300 border-red-900",
  "no-show": "bg-[rgba(140,140,140,0.15)] text-gray-300 border-gray-700",
};

export default function Appointments() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const prefillAppointment = params.get("appointment") || "";
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

   const goQuickSale = (a) => {
    const params = new URLSearchParams();
    if (a.id) params.set("appointment", a.id);
    if (a.customerId) params.set("customer", a.customerId);
    if (a.appointmentDate) params.set("date", a.appointmentDate);
    if (a.serviceId) params.set("service", a.serviceId);
    if (a.staffId) params.set("staff", a.staffId);
    const url = `/dashboard/quick-sale?${params.toString()}`;
    // eslint-disable-next-line no-console
    //console.log("[QuickSale] navigating to", url, "user role:", norm(user.roleName));
    navigate(url);
  };

  const load = async () => {
    setLoading(true);
    try {
      if (prefillAppointment) {
         const r = await api.get(`/appointments/customer/${prefillAppointment}`);
         setRows(r.data);
      } else{
        const r = await api.get("/appointments");
        setRows(r.data);
      }
    } catch (e) { toast.error("Failed to load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    try {
      api.patch(`/appointments/${id}/status?status=${status}`, { status });
      toast.success(`Marked ${status}`);
      load();
    } catch { toast.error("Failed"); }
  };

  const cancel = async (id) => {
    try { await api.delete(`/appointments/${id}`); toast.success("Cancelled"); load(); }
    catch { toast.error("Failed"); }
  };

  return (
    <div className="space-y-8" data-testid="appointments-page">
        <div>
          <div className="overline mb-2">Bookings</div>
          <h1 className="font-heading text-4xl text-white">Appointments</h1>
        </div>
      <div className="bg-emerald-surface border border-gold-subtle rounded-2xl overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-gold-subtle hover:bg-transparent">
              <TableHead className="text-emerald-muted">Guest</TableHead>
              <TableHead className="text-emerald-muted">Service</TableHead>
              <TableHead className="text-emerald-muted">Stylist</TableHead>
              <TableHead className="text-emerald-muted">Date & Time</TableHead>
              <TableHead className="text-emerald-muted">Status</TableHead>
              <TableHead className="text-emerald-muted text-right">Price</TableHead>
              <TableHead className="text-emerald-muted">Remarks</TableHead>
              <TableHead className="text-emerald-muted w-10"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && <TableRow><TableCell colSpan={8} className="text-center text-emerald-muted py-8">Loading…</TableCell></TableRow>}
            {!loading && rows.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-emerald-muted py-8">No appointments yet.</TableCell></TableRow>}
            {rows.map((a) => (
              <TableRow key={a.id} className="border-gold-subtle" data-testid={`appointment-row-${a.id}`}>
                <TableCell className="text-white">{a.customerName || "—"}</TableCell>
                <TableCell className="text-white">{a.serviceName || "—"}</TableCell>
                <TableCell className="text-emerald-muted">{a.staffName || "—"}</TableCell>
                <TableCell className="text-emerald-muted">{a.appointmentDate} · {a.startTime}</TableCell>
                <TableCell>
                  <Badge variant="outline" className={`${STATUS_COLORS[norm(a.status)] || ""} capitalize`}>{norm(a.status)}</Badge>
                </TableCell>
                <TableCell className="text-right text-gold">{formatINR(a.price)}</TableCell>
                <TableCell className="text-emerald-muted text-sm">{a.remarks || "—"}</TableCell>
                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button data-testid={`appointment-menu-${a.id}`} className="text-emerald-muted hover:text-gold p-1"><MoreVertical className="w-4 h-4"/></button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="bg-emerald-surface border-gold-subtle text-white">
                      {(norm(user.roleName) === "admin" || norm(user.roleName) === "staff" || norm(user.roleName) === "receptionist" ) && norm(a.status) === "scheduled" && (
                       <DropdownMenuItem
                          data-testid={`quick-sale-appt-${a.id}`}
                          onSelect={(e) => { e.preventDefault(); goQuickSale(a); }}
                          className="cursor-pointer">
                          <Zap className="w-4 h-4 text-gold mr-2" /> Quick Sale
                       </DropdownMenuItem>
                      )}
                      {(norm(user.roleName) === "admin" || norm(user.roleName) === "staff") && norm(a.status) === "scheduled" && (
                        <>
                          <DropdownMenuItem onClick={() => setStatus(a.id, "no-show")}>Mark no-show</DropdownMenuItem>
                        </>
                      )}
                      {norm(a.status) === "scheduled" && (
                        <DropdownMenuItem data-testid={`cancel-appt-${a.id}`} onClick={() => cancel(a.id)} className="text-red-300 focus:text-red-200">Cancel</DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
